import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { Location } from "history";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import { Link } from "react-router-dom";
import { convertListStatusToEntityStatus } from "src/utils/entityStatus/entityStatus";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import Tag from "go-app/components/Tags/Tag";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListFilterSource } from "go-list/core/components/Filter/services/types";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import AppFormatResourceStatusLabel from "../../../../../../../../../../components/FormatStatus";
import { TransferApi, TransferStatus } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import { TransferSummaryApi } from "../../../../services/types";
import { transferListName, transferResourceType } from "../../../../utils";
import { getConfigFieldsWithSummaryData } from "../../../../utils/summaryDataUtils";

const TranslationsInitialized = () => {
	const { t } = useTranslation();
	t("enums.multiple_actions.name.CLOSE");
	t("enums.multiple_actions.name.CANCEL");
	t("enums.multiple_actions.name.CONFIRM");
	t("enums.multiple_actions.name.REMOVE");
	t("enums.multiple_actions.name.CANCEL_CLOSE");
	t("enums.multiple_actions.name.VOID_OPEN_RECEIVED");
	t("enums.multiple_actions.name.VOID_OPEN");
	t("enums.multiple_actions.name.VOID_RECEIVED");
};

interface ListState {
	resource?: Record<string, any>;
	path: string;
	mobileActions: MobileActionProps[];
}

const buildConfig = (
	t: TFunction,
	onPerformAction: (
		id: number | string,
		request: (transferId: any) => Promise<TransferApi> | Promise<TransferApi[]>,
		confirmationWarning?: string,
		confirmationMessage?: string,
		flashMessage?: string,
		areAllItemsSelected?: boolean,
		multipleActionsParams?: MultipleActionsParams
	) => Promise<void>,
	data: Record<string, any>,
	location: Location,
	segmentListService: SegmentListService,
	summaryData: TransferSummaryApi | undefined,
	setSummaryData: (summaryData: TransferSummaryApi) => void,
	organization: OrganizationState
) => {
	let config = {
		fields: [
			{
				id: "transferred_at",
				name: t("common.word.date", { ns: "lib" }),
				type: "date",
				render: (item: TransferApi) => {
					return (
						<StickyColumnWithEntityStatus
							status={convertListStatusToEntityStatus(item.status)}
							to={`${location.pathname}/${item.id}`}
						>
							{FormatDate(item.transferred_at)}
						</StickyColumnWithEntityStatus>
					);
				},
				renderExport: (item: TransferApi) => FormatDate(item.transferred_at),
			},
			{
				id: "created_at",
				name: t("common.word.created_at", { ns: "lib" }),
				type: "date",
				render: (item: TransferApi) => {
					return FormatDate(item.created_at);
				},
			},
			{
				id: "document_number",
				name: t("common.word.number", { ns: "lib" }),
				render: (item: TransferApi) => (
					<Link to={`${location.pathname}/${item.id}`}>{item?.document_number}</Link>
				),
				type: "text",
				typeOptions: ["c", "e"],
			},
			{
				id: "source_stock.name",
				name: t("modules.transfer.field.source_stock.title"),
				render: (item: TransferApi) => (
					<RenderLimitedText minWidth={100}>{item?.source_stock?.name}</RenderLimitedText>
				),
				renderExport: (item: TransferApi) => item?.source_stock?.name,
			},
			{
				id: "destination_stock.name",
				name: t("modules.transfer.field.destination_stock.title"),
				render: (item: TransferApi) => (
					<RenderLimitedText minWidth={100}>{item?.destination_stock?.name}</RenderLimitedText>
				),
				renderExport: (item: TransferApi) => item?.destination_stock?.name,
			},
			{
				id: "status",
				name: t("common.word.status", { ns: "lib" }),
				render: (item: TransferApi) => <AppFormatResourceStatusLabel status={item.status} />,
				renderExport: (item: TransferApi) => t(`enums.common.status.${item.status}`),
			},
			{
				id: "comment",
				name: t("common.word.comment.title"),
				type: "text",
				render: (item: TransferApi) => {
					return <RenderLimitedText minWidth={200}>{item.comment}</RenderLimitedText>;
				},
				renderExport: (item: TransferApi) => item.comment,
			},
			{
				id: "tagQuery",
				name: t("common.word.tags"),
				render: (item: TransferApi) => (
					<div className="d-flex flex-wrap">
						{item?.tags?.map((tag, i) => (
							<Tag className="mb-1" key={`tag-${i}`} title={tag.name} />
						))}
					</div>
				),
				renderExport: (item: TransferApi) => item?.tags?.map((tag) => tag.name).join(", "),
			},
			{
				id: "total_fifo_cost_net_money",
				name: t("common.word.cost"),
				type: "number",
				render: (item: TransferApi) => FormatMoney(item.total_fifo_cost_net_money),
			},
			{
				id: "reference_number",
				name: t("common.word.reference_number"),
				type: "text",
			},
			{
				id: "created_by",
				name: t("common.word.created_by"),
				render: (item: TransferApi) => item.created_by || t("common.word.system"),
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: TransferApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: TransferApi) => `${location.pathname}/${item.id}/edit`,
			},
			{
				name: t("common.action.confirm", { ns: "lib" }),
				click: (item: TransferApi) => onPerformAction(item.id, api.organization().confirmTransfer.bind(api)),
				visible: (item: TransferApi) => item.status === "DRAFT",
				refreshListAfter: true,
			},
			{
				name: t("common.action.close", { ns: "lib" }),
				click: (item: TransferApi) => onPerformAction(item.id, api.organization().closeTransfer.bind(api)),
				visible: (item: TransferApi) => item.status === "OPEN",
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel_close"),
				click: (item: TransferApi) =>
					onPerformAction(item.id, api.organization().closeCancelTransfer.bind(api), "", "cancel"),
				visible: (item: TransferApi) => item.status === "RECEIVED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel", { ns: "lib" }),
				click: (item: TransferApi) =>
					onPerformAction(item.id, api.organization().cancelTransfer.bind(api), "", "cancel"),
				visible: (item: TransferApi) => item.status === "OPEN",
				refreshListAfter: true,
			},
			{
				name: t("common.action.void"),
				click: (item: TransferApi) =>
					onPerformAction(item.id, api.organization().removeTransfer.bind(api), "", "remove", "removed"),
				visible: (item: TransferApi) => item.status !== "VOID" && item.status !== "DRAFT",
				refreshListAfter: true,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: TransferApi) =>
					onPerformAction(item.id, api.organization().removeTransfer.bind(api), "", "remove", "removed"),
				visible: (item: TransferApi) => item.status === "DRAFT" || item.status === "VOID",
				refreshListAfter: true,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: (item: TransferApi) =>
					onPerformAction(item.id, api.organization().restoreTransfer.bind(api), "", "action", "restored"),
				visible: (item: TransferApi) => item.status === "VOID",
				refreshListAfter: true,
			},
		],
		filters: [
			{
				id: "tagQuery",
				name: t("common.word.tags"),
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						const tags = await api
							.organization()
							.getTagSearchSelect(search, params, { cancelToken: options?.token });

						return tags.map((tag) => ({
							id: tag.label,
							label: tag.label,
						}));
					},
				} as ListFilterSource,
			},
			{
				id: "sourceStock",
				name: t("modules.transfer.field.source_stock.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getStockSearchSelect(search, params, { cancelToken: options?.token }),
				} as ListFilterSource,
			},
			{
				id: "destinationStock",
				name: t("modules.transfer.field.destination_stock.title"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getStockSearchSelect(search, params, { cancelToken: options?.token }),
				} as ListFilterSource,
			},
			{
				id: "statuses",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				listOptions: {
					DRAFT: t("enums.common.status.DRAFT"),
					OPEN: t("enums.common.status.OPEN"),
					RECEIVED: t("enums.common.status.RECEIVED"),
					VOID: t("enums.common.status.VOID"),
				},
				hasDefaultOptions: false,
			},
		],
		selectedColumns: [
			"transferred_at",
			"document_number",
			"reference_number",
			"source_stock.name",
			"destination_stock.name",
			"status",
			"tagQuery",
			"total_fifo_cost_net_money",
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		multipleActions: [
			{
				name: t("common.action.confirm", { ns: "lib" }),
				click: async (
					items: TransferApi[],
					_?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items
						.filter((item) => item.status === "DRAFT")
						.map((item) => item.id)
						.join(",");
					await onPerformAction(
						ids,
						api.organization().confirmMultipleTransfers.bind(api),
						"CONFIRM",
						undefined,
						undefined,
						areAllItemsSelected,
						multipleActionsParams
					);
				},
				visible: (items: TransferApi[]) => items.filter((f) => f.status === "DRAFT").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.close", { ns: "lib" }),
				click: async (
					items: TransferApi[],
					_?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items
						.filter((item) => item.status === "OPEN")
						.map((item) => item.id)
						.join(",");
					await onPerformAction(
						ids,
						api.organization().closeMultipleTransfers.bind(api),
						"CLOSE",
						undefined,
						undefined,
						areAllItemsSelected,
						multipleActionsParams
					);
				},
				visible: (items: TransferApi[]) => items.filter((f) => f.status === "OPEN").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel_close"),
				click: async (
					items: TransferApi[],
					_?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items
						.filter((item) => item.status === "RECEIVED")
						.map((item) => item.id)
						.join(",");
					await onPerformAction(
						ids,
						api.organization().cancelCloseMultipleTransfers.bind(api),
						"CANCEL_CLOSE",
						undefined,
						undefined,
						areAllItemsSelected,
						multipleActionsParams
					);
				},
				visible: (items: TransferApi[]) => items.filter((f) => f.status === "RECEIVED").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel", { ns: "lib" }),
				click: async (
					items: TransferApi[],
					_?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items
						.filter((item) => item.status === "OPEN")
						.map((item) => item.id)
						.join(",");
					await onPerformAction(
						ids,
						api.organization().cancelMultipleTransfers.bind(api),
						"CANCEL",
						undefined,
						undefined,
						areAllItemsSelected,
						multipleActionsParams
					);
				},
				visible: (items: TransferApi[]) => items.filter((f) => f.status === "OPEN").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.void"),
				click: async (
					items: TransferApi[],
					_?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const itemsReadyToAction = items.filter(
						(item) => item.status !== "VOID" && item.status !== "DRAFT"
					);
					const ids = itemsReadyToAction.map((item) => item.id).join(",");
					const statuses = itemsReadyToAction.map((item) => item.status);
					let warningMessage = "";
					if (statuses.includes(TransferStatus.OPEN) && statuses.includes(TransferStatus.RECEIVED))
						warningMessage = "VOID_OPEN_RECEIVED";
					else if (statuses.includes(TransferStatus.OPEN)) warningMessage = "VOID_OPEN";
					else warningMessage = "VOID_RECEIVED";
					await onPerformAction(
						ids,
						api.organization().removeMultipleTransfers.bind(api),
						warningMessage,
						undefined,
						undefined,
						areAllItemsSelected,
						multipleActionsParams
					);
				},
				visible: (items: TransferApi[]) =>
					items.filter((f) => f.status !== "VOID" && f.status !== "DRAFT").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: async (
					items: TransferApi[],
					_?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items
						.filter((item) => item.status === "DRAFT" || item.status === "VOID")
						.map((item) => item.id)
						.join(",");
					await onPerformAction(
						ids,
						api.organization().removeMultipleTransfers.bind(api),
						undefined,
						"remove",
						"removed",
						areAllItemsSelected,
						multipleActionsParams
					);
				},
				visible: (items: TransferApi[]) =>
					items.filter((f) => f.status === "DRAFT" || f.status === "VOID").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: async (
					items: TransferApi[],
					_?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items
						.filter((item) => item.status === "VOID")
						.map((item) => item.id)
						.join(",");
					await onPerformAction(
						ids,
						api.organization().restoreMultipleTransfers.bind(api),
						undefined,
						undefined,
						"restored",
						areAllItemsSelected,
						multipleActionsParams
					);
				},
				visible: (items: TransferApi[]) => items.filter((f) => f.status === "VOID").length > 0,
				refreshListAfter: true,
			},
		],
		exportConfig: {
			title: t("modules.transfer.field.export_config.title"),
			filename: t("modules.transfer.header.title"),
			organization: organization.name,
		},
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "custom_fields,source_stock,destination_stock,tags,contact";
			const response = await api.organization().getTransfers(params, { cancelToken: sourceToken?.token });

			setSummaryData(response.summary);
			return response;
		},
		fetchTotalItemsCountForSelectedFilters: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "all";
			return api.organization().getTransfersCount(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentListService.save(transferListName, transferResourceType, segment);
		},
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
	} as ListConfig;

	const summaryDataConfig = {
		total_fifo_cost_net_money: FormatMoney(summaryData?.total_fifo_cost_net_money),
	};

	config.fields = getConfigFieldsWithSummaryData(t, summaryDataConfig, config.fields, summaryData);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};

const List: FC<ListState> = ({ resource, path, mobileActions }) => {
	const [summaryData, setSummaryData] = useState<TransferSummaryApi | undefined>(undefined);
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const confirmation = useConfirmation();
	const { addSuccessFlash } = useFlash();
	const segmentContextValue = useContext(SegmentContext);
	const organization = useSelector(selectOrganization);

	const onPerformAction = async (
		id: number | string,
		request: (
			id: any,
			multipleActionParams?: MultipleActionsParams,
			areAllItemsSelected?: boolean
		) => Promise<TransferApi> | Promise<TransferApi[]>,
		confirmationWarning = "",
		confirmationMessage = "action",
		flashMessage = "completed",
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: `${confirmationWarning ? `${t(`enums.multiple_actions.name.${confirmationWarning}`)} ` : ""}${t(
				`confirmation.message.${confirmationMessage}`,
				{ ns: "lib" }
			)}`,
		});

		if (typeof id === "number") await request(id as number);
		else await request(id as string, multipleActionParams, areAllItemsSelected);

		addSuccessFlash(t(`common.flash.${flashMessage}`, { ns: "lib" }));
	};

	if (!resource) return null;
	const data = resource.read();
	const config = buildConfig(
		t,
		onPerformAction,
		data,
		location,
		segmentContextValue,
		summaryData,
		setSummaryData,
		organization
	);
	return (
		<ListData
			config={config}
			emptyList={{ addAction: () => history.push(`${path}/new`) }}
			canManageMultiActionsForAllItems
			mobileActions={mobileActions}
			shouldChangeSegmentFromOutside
		/>
	);
};

const OrganizationTransferIndexPage: FC<RouteComponentProps> = (props) => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: transferResourceType,
			name: t(`enums.custom_fields.resources.TRANSFER_ORDER`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.transfer.header.title"));
		setResource(
			wrapPromise(segmentContextValue.get(transferListName, transferResourceType, customFieldResourceTypes))
		);
	}, []);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			variant: "primary",
			path: `${props.match.url}/new`,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${props.match.url}/new`,
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.transfer.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} path={props.match.url} mobileActions={mobileActions} {...props} />
			</Suspense>
		</>
	);
};

export default OrganizationTransferIndexPage;
